import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { useLayoutEffect, useState } from 'react';
import * as yup from 'yup';
import { AlertState } from '../../abelcommon';
import { ConditionalTooltip } from '../../abelcommon/components/ConditionalTooltip';
import { loadBalancerAPI } from '../../api/load-balancer';
import {
  PasswordValidationError,
  PasswordValidationWarning,
  passwordValidationSchema,
} from '../../components/PasswordValidationWarning';
import { StyledSmartLink } from '../../components/common';
import { useTranslation } from 'react-i18next';

export const RegisterPage = () => {
  const theme = useTheme();
  const [alertState, setAlertState] = useState<AlertState>({
    isOpen: false,
    severity: 'success',
    message: '',
  });
  const [getCodeCount, setGetCodeCount] = useState(0);
  const [timer, setTimer] = useState<number>(0);
  const { t } = useTranslation();

  const validationSchema = yup.object({
    email: yup
      .string()
      .trim()
      .email(t('registerPage.pleaseEnterValidEmail'))
      .required(t('registerPage.pleaseEnterEmail')),
    password: passwordValidationSchema,
    confirmPassword: yup
      .string()
      .trim()
      .oneOf([yup.ref('password'), null], t('registerPage.confirmPasswordNotMatch')),
    verificationCode: yup.string().trim().required(t('registerPage.pleaseEnterTheVerificationCode')),
  });

  useLayoutEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev === 1) clearInterval(interval);
        return prev > 0 ? prev - 1 : 0;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [getCodeCount]);

  const {
    mutate: mutateGetRegisterVerificationCode,
    isLoading: isGetRegisterVerificationCodeLoading,
    data: getRegisterVerificationCodeData,
  } = loadBalancerAPI.hookWrapperMutation.useGetRegisterVerificationCode();
  const {
    mutate: mutateRegister,
    isLoading: isRegisterLoading,
    error: registerError,
  } = loadBalancerAPI.hookWrapperMutation.useRegister();

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      verificationCode: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      mutateRegister(
        {
          email: values.email.trim(),
          password: values.password.trim(),
          confirmPassword: values.confirmPassword.trim(),
          verificationCode: values.verificationCode.trim(),
        },
        {
          onSuccess: () => {
            resetForm();
            setAlertState((prev) => ({
              ...prev,
              isOpen: true,
              severity: 'success',
              autoHideDuration: null,
              message: t('registerPage.registerSuccess'),
            }));
          },
        }
      );
    },
  });

  const handleGetVerificationCode = () => {
    if (timer > 0) return;

    const trimmedEmail = formik.values.email.trim();
    if (!trimmedEmail.trim()) {
      formik.setFieldTouched('email', true, true);
      return;
    }
    mutateGetRegisterVerificationCode(trimmedEmail, {
      onSuccess: () => {
        setTimer(120);
        setGetCodeCount((prev) => prev + 1);
        setAlertState((prev) => ({
          ...prev,
          isOpen: true,
          severity: 'success',
          message: t('registerPage.enterCodeNotification'),
        }));
      },
      onError: (error) => {
        formik.setFieldTouched('email', true, false);
        formik.setFieldError('email', error.message);
      },
    });
  };

  return (
    <Container maxWidth="md">
      <Card>
        <CardContent sx={{ overflow: 'hidden', py: 5 }}>
          {registerError && <Alert severity="error">{registerError.message}</Alert>}
          <Box
            sx={{
              mx: 'auto',
              [theme.breakpoints.up('md')]: { py: 5, width: '60%' },
              [theme.breakpoints.down('md')]: { width: '90%' },
            }}
          >
            <Typography variant="h3" sx={{ mb: 4, fontSize: '2rem' }}>
              {t('registerPage.register')}
            </Typography>
            <form onSubmit={formik.handleSubmit}>
              <Stack sx={{ gap: 2 }}>
                <TextField
                  id="email"
                  name="email"
                  label={t('registerPage.email')}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  size="small"
                />
                <FormControl>
                  <InputLabel
                    size="small"
                    htmlFor="email-verify-code"
                    error={formik.touched.verificationCode && Boolean(formik.errors.verificationCode)}
                  >
                    {t('registerPage.emailVerificationCode')}
                  </InputLabel>
                  <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                    <OutlinedInput
                      sx={{ flex: 1 }}
                      id="email-verify-code"
                      name="verificationCode"
                      label={t('registerPage.emailVerificationCode')}
                      value={formik.values.verificationCode}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.verificationCode && Boolean(formik.errors.verificationCode)}
                      size="small"
                    />
                    <ConditionalTooltip
                      shouldShow={!!formik.errors.email || !formik.values.email}
                      title={t('registerPage.pleaseEnterAValidEmailAddress')}
                      arrow
                    >
                      <div>
                        <LoadingButton
                          variant="outlined"
                          color="info"
                          size="small"
                          disabled={!!formik.errors.email || !formik.values.email || timer > 0}
                          loading={isGetRegisterVerificationCodeLoading}
                          onClick={handleGetVerificationCode}
                          sx={{ height: '100%', minWidth: '83px' }}
                        >
                          {getCodeCount > 0 ? t('registerPage.resend') : t('registerPage.getCode')}
                        </LoadingButton>
                      </div>
                    </ConditionalTooltip>
                  </Stack>
                  {timer > 0 && (
                    <FormHelperText>
                      {t('registerPage.didntReceiveACode')} {timer} {t('registerPage.seconds')}.
                    </FormHelperText>
                  )}
                  <FormHelperText error={formik.touched.verificationCode && Boolean(formik.errors.verificationCode)}>
                    {formik.touched.verificationCode && formik.errors.verificationCode}
                  </FormHelperText>
                </FormControl>
                <TextField
                  id="password"
                  name="password"
                  label={t('registerPage.password')}
                  type="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  FormHelperTextProps={{ component: 'div' }}
                  helperText={
                    formik.touched.password && formik.errors.password ? (
                      <PasswordValidationWarning error={formik.errors.password as unknown as PasswordValidationError} />
                    ) : undefined
                  }
                  size="small"
                />
                <TextField
                  id="confirm-password"
                  name="confirmPassword"
                  label={t('registerPage.confirmPassword')}
                  type="password"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                  helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                  size="small"
                />
                <LoadingButton
                  loading={isRegisterLoading}
                  color="primary"
                  variant="contained"
                  type="submit"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  {t('registerPage.register')}
                </LoadingButton>
              </Stack>
            </form>
            <Box sx={{ mt: 2 }}>
              <hr />
            </Box>
            <Stack sx={{ flexDirection: 'row', justifyContent: 'center', mt: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {t('registerPage.alreadyHaveAccount')}{' '}
                <StyledSmartLink to="/auth/login">{t('registerPage.loginNow')}</StyledSmartLink>
              </Typography>
            </Stack>
          </Box>
        </CardContent>
      </Card>
      <Snackbar
        open={alertState.isOpen}
        autoHideDuration={alertState.autoHideDuration === undefined ? 10000 : alertState.autoHideDuration}
        onClose={(e, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setAlertState((prev) => ({ ...prev, isOpen: false }));
        }}
      >
        <Alert
          onClose={() => setAlertState((prev) => ({ ...prev, isOpen: false }))}
          severity={alertState.severity}
          variant="filled"
          sx={{ width: '100%', color: 'white' }}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
